"use client";

import type React from "react";
import { Button } from "@/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";

type ButtonSize = NonNullable<React.ComponentProps<typeof Button>["size"]>;
type IconButtonSize = Exclude<ButtonSize, `icon${string}`>;

const ICON_SIZES = {
  default: "icon",
  xs: "icon-xs",
  sm: "icon-sm",
  lg: "icon-lg",
} as const satisfies Record<IconButtonSize, ButtonSize>;

// A string tooltip doubles as the accessible name; any other tooltip content
// has no reliable text equivalent, so an explicit `aria-label` is required.
type IconButtonLabelProps =
  | { tooltipText: string; "aria-label"?: string }
  | {
      tooltipText: Exclude<React.ReactNode, string>;
      "aria-label": string;
    };

type IconButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "aria-label" | "children" | "size"
> &
  IconButtonLabelProps & {
    /** The icon to render. Icon-only buttons carry no visible text. */
    children: React.ReactNode;
    /** Same scale as `Button`; renders a square button of matching height. */
    size?: IconButtonSize;
  };

/**
 * An icon-only button that describes its action in a tooltip on hover and
 * focus. Accepts every `Button` prop, so any variant or size can be used.
 */
function IconButton({
  id,
  tooltipText,
  "aria-label": ariaLabel,
  size = "default",
  children,
  ...props
}: IconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        id={id}
        render={
          <Button
            aria-label={
              ariaLabel ??
              (typeof tooltipText === "string" ? tooltipText : undefined)
            }
            size={ICON_SIZES[size]}
            {...props}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

export type { IconButtonProps };
export { IconButton };
